/**
 * The wire shapes the API actually returns.
 *
 * `aws-backend/src/types/api.ts` described an older, narrower contract: the
 * handlers had grown `source`, `status`, `authAmount`, and the rest of the
 * pending-transaction fields without it noticing. These are the real shapes,
 * verified against `handlers/snapshot.ts`.
 */

export type BudgetItemKind = 'envelope' | 'bill';

/**
 * Which paychecks put money into a budget item.
 *
 * Most things take half from each. A bill too big to sit half-funded — rent due
 * on the 1st — instead takes its whole monthly cost out of one paycheck, and
 * `perPaycheckAmount` is then that whole cost.
 */
export type FundingSchedule = 'everyPaycheck' | 'firstPaycheck' | 'secondPaycheck' | 'onDay';

export interface BudgetItem {
  id: string;
  kind: BudgetItemKind;
  name: string;
  icon: string;
  color: string;
  category: string;
  isActive: boolean;
  isFund: boolean;
  /** Kept on Home when true. Missing on older rows means not pinned. */
  isPinned?: boolean;
  /**
   * What this item is funded with out of one paycheck. Over a month that is
   * twice this, unless `fundingSchedule` says only one paycheck pays into it.
   */
  perPaycheckAmount: number;
  /**
   * Which paychecks fund the item. Missing on rows written before the field
   * existed, which is why every reader falls back to `everyPaycheck` — the only
   * arrangement the app could express until then.
   */
  fundingSchedule?: FundingSchedule | null;
  /** Day of the month this item refills when `fundingSchedule` is `onDay`. */
  fundingDay?: number | null;
  dueDay?: number | null;
  notes?: string | null;
  sortOrder: number;
  /**
   * Parent envelope or bill this item breaks apart. One level only.
   * Spend on a child counts against the parent. Children are not in the
   * paycheck plan total — the parent still holds the funded amount.
   */
  parentId?: string | null;
  /** ISO 8601. Missing or null means the box stays on the live lists. */
  expiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/**
 * `joined` marks a pending sighting that was folded into its posted twin, so it
 * is history rather than a live row.
 */
export type TransactionStatus = 'pending' | 'posted' | 'joined' | 'voided';

export type LifecycleEventKind =
  | 'pending'
  | 'posted'
  | 'unlinked'
  | 'unresolved'
  | 'adjusted'
  | 'waiting'
  | 'direct';

export interface LifecycleEvent {
  kind: LifecycleEventKind | string;
  at?: string | null;
  joinedId?: string | null;
  amount?: number | null;
  date?: string | null;
}

export interface Transaction {
  id: string;
  /** Ledger date, `YYYY-MM-DD` in Eastern time. Never a timestamp. */
  transactionDate: string;
  /** The bank's raw line. Use `merchantName()` before showing it to anyone. */
  description: string;
  /** Negative for money out, positive for money in. */
  amount: number;
  type: string;
  balance?: number | null;
  category?: string | null;
  isNew: boolean;
  envelopeId?: string | null;
  billId?: string | null;
  isSplit: boolean;
  deletedAt?: string | null;
  deletedReason?: string | null;
  source?: string | null;
  accountId?: string | null;
  status: TransactionStatus;
  authAmount?: number | null;
  postedAmount?: number | null;
  postedDate?: string | null;
  joinedIntoId?: string | null;
  lifecycleEvents?: LifecycleEvent[] | string | null;
  firstSeenAt?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  locationAccuracy?: number | null;
  locationCapturedAt?: string | null;
  locationAccountId?: string | null;
  locationEmail?: string | null;
  locationDisplayName?: string | null;
  locationSource?: 'presence' | 'arrival' | 'manual' | null;
  locationConfirmed?: boolean | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** Live GPS fix for a signed-in household member. */
export interface Presence {
  cognitoSub: string;
  email: string;
  displayName: string;
  latitude: number;
  longitude: number;
  accuracy?: number | null;
  updatedAt: string;
  sharingEnabled: boolean;
}

export interface Allocation {
  id: string;
  transactionId: string;
  budgetItemId: string;
  /** Always positive. Direction comes from the transaction it belongs to. */
  amount: number;
  notes?: string | null;
  createdAt: string;
}

export interface PaycheckSettings {
  paycheckAmount: number;
  envelopeAllocation: number;
  billAllocation: number;
  payDays?: number[];
  /**
   * Ledger date (`YYYY-MM-DD`) after which fund leftover starts counting.
   * A previous funding window that began before this day contributes $0 carry.
   * Null/undefined keeps the old behavior (useful in tests).
   */
  rolloverKickoffDate?: string | null;
  updatedAt: string;
  version: number;
}

export interface SortingRule {
  id: string;
  keyword: string;
  budgetItemId: string;
  createdFromDescription?: string | null;
  isActive: boolean;
  createdAt: string;
}

/** Everything a client needs for a cold start, in one request. */
export interface Snapshot {
  budgetItems: BudgetItem[];
  transactions: Transaction[];
  allocations: Allocation[];
  sortingRules: SortingRule[];
  paycheckSettings: PaycheckSettings;
  serverTime: string;
}

export const DEFAULT_PAYCHECK_SETTINGS: Omit<PaycheckSettings, 'updatedAt' | 'version'> = {
  paycheckAmount: 4180.03,
  envelopeAllocation: 2224.11,
  billAllocation: 1955.92,
  payDays: [1, 16],
};

/**
 * DynamoDB stores lifecycle events as a list, but older rows and the scraper
 * wrote a JSON string. Accept either rather than throwing away join history.
 */
export function lifecycleEventsOf(transaction: Transaction): LifecycleEvent[] {
  const raw = transaction.lifecycleEvents;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function isDeleted(transaction: Transaction): boolean {
  return transaction.deletedAt != null;
}

export function isPending(transaction: Transaction): boolean {
  return transaction.status === 'pending';
}

export function isPosted(transaction: Transaction): boolean {
  return transaction.status === 'posted';
}
